import React, { FC, useMemo } from 'react';
import { Bar, Chart } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { incidentBandsPlugin } from './incidentBandsPlugin';
import {
  fmtDay,
  incidentBands,
  KIND_COLOR,
  KIND_LABEL,
  KINDS,
  weekdayLabel,
} from './opsCapacityUtils';
import type {
  OpsCapacityDayRow,
  OpsCapacityHeatCell,
  OpsCapacityHourRow,
  OpsCapacityIncident,
  OpsCapacityWeekdayRow,
  OpsCapacityWeekRow,
} from './types';
import { Empty } from './OpsCapacitySection';

const GRID = 'rgba(148, 163, 184, 0.15)';

const stackedOpts = (
  extra?: Partial<ChartOptions<'bar'>>,
): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
    ...extra?.plugins,
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      ticks: { font: { size: 10 } },
    },
    y: { stacked: true, beginAtZero: true, grid: { color: GRID } },
    ...extra?.scales,
  },
});

const kindDatasets = <T,>(
  rows: T[],
  pick: (row: T, kind: (typeof KINDS)[number]) => number,
) =>
  KINDS.map((k) => ({
    type: 'bar' as const,
    label: KIND_LABEL[k],
    data: rows.map((r) => pick(r, k)),
    backgroundColor: KIND_COLOR[k],
    borderRadius: 2,
    stack: 'demand',
  }));

interface WeeklyProps {
  weekly: OpsCapacityWeekRow[];
  incidents: OpsCapacityIncident[];
}

/** Stacked weekly demand by stream with WAU on a second axis. */
export const WeeklyDemandChart: FC<WeeklyProps> = ({ weekly, incidents }) => {
  const bands = useMemo(
    () =>
      incidentBands(
        incidents,
        weekly.map((w) => w.weekStart),
        'week',
      ),
    [incidents, weekly],
  );
  if (weekly.length === 0) return <Empty />;
  const labels = weekly.map(
    (w) => `${fmtDay(w.weekStart)}${w.complete ? '' : ' *'}`,
  );
  const data: ChartData<'bar' | 'line', number[], string> = {
    labels,
    datasets: [
      ...kindDatasets(weekly, (r, k) => r[k]),
      {
        type: 'line',
        label: 'Weekly active users',
        data: weekly.map((w) => w.wau),
        borderColor: 'rgb(15, 23, 42)',
        backgroundColor: 'rgb(15, 23, 42)',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.3,
        yAxisID: 'wau',
      },
    ],
  };
  const options: ChartOptions<'bar' | 'line'> = {
    ...stackedOpts({ plugins: { incidentBands: { bands } } }),
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { font: { size: 10 } },
      },
      y: { stacked: true, beginAtZero: true, grid: { color: GRID } },
      wau: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: { display: false },
        title: { display: true, text: 'WAU', font: { size: 10 } },
      },
    },
  };
  return (
    <>
      <div className='ops-chart ops-chart--tall'>
        <Chart
          type='bar'
          plugins={[incidentBandsPlugin]}
          options={options}
          data={data}
        />
      </div>
      {weekly.some((w) => !w.complete) && (
        <p className='ops-chart-note'>
          * partial week (range edge or current week)
        </p>
      )}
    </>
  );
};

interface DailyProps {
  daily: OpsCapacityDayRow[];
  incidents: OpsCapacityIncident[];
  today: string;
}

/** Stacked daily demand; the current London day is marked as partial. */
export const DailyDemandChart: FC<DailyProps> = ({
  daily,
  incidents,
  today,
}) => {
  const bands = useMemo(
    () =>
      incidentBands(
        incidents,
        daily.map((d) => d.date),
        'day',
      ),
    [incidents, daily],
  );
  if (daily.length === 0) return <Empty />;
  const labels = daily.map(
    (d) => `${fmtDay(d.date)}${d.date === today ? ' *' : ''}`,
  );
  return (
    <>
      <div className='ops-chart ops-chart--tall'>
        <Bar
          plugins={[incidentBandsPlugin]}
          options={stackedOpts({
            plugins: { incidentBands: { bands } },
            scales: {
              x: {
                stacked: true,
                grid: { display: false },
                ticks: {
                  font: { size: 10 },
                  maxTicksLimit: 31,
                  autoSkip: true,
                },
              },
            },
          })}
          data={{ labels, datasets: kindDatasets(daily, (r, k) => r[k]) }}
        />
      </div>
      {daily.some((d) => d.date === today) && (
        <p className='ops-chart-note'>* today so far (Europe/London)</p>
      )}
    </>
  );
};

export const WeekdayProfileChart: FC<{ rows: OpsCapacityWeekdayRow[] }> = ({
  rows,
}) => {
  if (rows.every((r) => KINDS.every((k) => r[k] === 0))) return <Empty />;
  return (
    <div className='ops-chart'>
      <Bar
        options={stackedOpts()}
        data={{
          labels: rows.map((r) => weekdayLabel(r.isoWeekday)),
          datasets: kindDatasets(rows, (r, k) => r[k]),
        }}
      />
    </div>
  );
};

export const HourProfileChart: FC<{ rows: OpsCapacityHourRow[] }> = ({
  rows,
}) => {
  if (rows.every((r) => KINDS.every((k) => r[k] === 0))) return <Empty />;
  return (
    <div className='ops-chart'>
      <Bar
        options={stackedOpts()}
        data={{
          labels: rows.map((r) => `${String(r.hour).padStart(2, '0')}`),
          datasets: kindDatasets(rows, (r, k) => r[k]),
        }}
      />
    </div>
  );
};

/** 7 × 24 grid of total demand; colour intensity is relative to the busiest cell. */
export const DemandHeatmap: FC<{ cells: OpsCapacityHeatCell[] }> = ({
  cells,
}) => {
  const max = cells.reduce((m, c) => Math.max(m, c.total), 0);
  if (max === 0) return <Empty />;
  const byKey = new Map(
    cells.map((c) => [`${c.isoWeekday}-${c.hour}`, c.total]),
  );
  const hours = Array.from({ length: 24 }, (_, h) => h);
  return (
    <div
      className='ops-heatmap'
      role='table'
      aria-label='Demand by weekday and hour'
    >
      <div className='ops-heatmap-row ops-heatmap-head' role='row'>
        <span className='ops-heatmap-label' />
        {hours.map((h) => (
          <span key={h} className='ops-heatmap-hour' role='columnheader'>
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
      </div>
      {[1, 2, 3, 4, 5, 6, 7].map((wd) => (
        <div key={wd} className='ops-heatmap-row' role='row'>
          <span className='ops-heatmap-label' role='rowheader'>
            {weekdayLabel(wd)}
          </span>
          {hours.map((h) => {
            const v = byKey.get(`${wd}-${h}`) ?? 0;
            const alpha = v === 0 ? 0 : 0.12 + 0.88 * (v / max);
            return (
              <span
                key={h}
                role='cell'
                className='ops-heatmap-cell'
                style={{
                  background: `rgba(59, 130, 246, ${alpha.toFixed(2)})`,
                }}
                title={`${weekdayLabel(wd)} ${String(h).padStart(2, '0')}:00 London — ${v} item${v === 1 ? '' : 's'}`}
              >
                {v > 0 && v === max ? v : ''}
              </span>
            );
          })}
        </div>
      ))}
      <p className='ops-chart-note'>
        Hours are Europe/London. Shift boundaries: 0–8, 8–16, 16–21, 21–24.
      </p>
    </div>
  );
};
